import { useCallback } from "react";
import { generateMarketingDocument } from "@/network/network";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys";
import { showToast } from "@/other/utils/utils";
import { getApiErrorMessage } from "@/integrations";
import type { MarketingDocumentFormat } from "@/bryntum-lib/models/MarketingDocument";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import { useAsyncTaskUIContext } from "@/modern/components/async-tasks/async-task-ui";
import {
  extractAsyncTaskId,
  matchBoardMarketingDocumentGenerationTask,
} from "@/modern/components/async-tasks/utils";

export function useMarketingDocumentGenerate(workFileId?: number) {
  const { selectedBoard } = useSwivuBryntumContext();
  const { pinTask } = useAsyncTaskContext();
  const { taskMatching } = useAsyncTaskUIContext();
  const boardId = (selectedBoard?.record as { id?: number } | undefined)?.id;

  const generating =
    boardId != null &&
    taskMatching((task) =>
      matchBoardMarketingDocumentGenerationTask(task, boardId, workFileId),
    );

  const startGenerateForWorkFile = useCallback(
    async (
      targetWorkFileId: number,
      format: MarketingDocumentFormat,
      name: string,
    ) => {
      const trimmedName = name.trim();
      if (!boardId || !trimmedName) {
        showToast({
          msg: !boardId ? "No board selected." : "Document name is required.",
          type: "error",
          duration: 3000,
        });
        return false;
      }
      try {
        const response = await generateMarketingDocument(boardId, {
          workFileId: targetWorkFileId,
          format,
          name: trimmedName,
        });
        const taskId = extractAsyncTaskId(response.data);
        const ok =
          response?.status === 202 ||
          response?.status === 200 ||
          taskId != null;
        if (!ok) {
          showToast({
            msg: "Could not start document generation.",
            type: "error",
            duration: 3000,
          });
          return false;
        }
        if (taskId != null) {
          pinTask(taskId, {
            type: ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION,
            meta_data: {
              board_id: boardId,
              work_file_id: targetWorkFileId,
              format,
              name: trimmedName,
            },
          });
        }
        return true;
      } catch (err: unknown) {
        showToast({
          msg: getApiErrorMessage(err, "Failed to start document generation."),
          type: "error",
          duration: 4000,
        });
        return false;
      }
    },
    [boardId, pinTask],
  );

  return { generating, startGenerateForWorkFile };
}
